import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { testSupabaseConnection } from './utils/supabaseTest';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/Landing.jsx';
import AboutUs from './pages/AboutUs.jsx';
import FAQ from './pages/FAQ.jsx';
import Science from './pages/Science.jsx';
import Technicians from './pages/Technicians.jsx';
import LBWProjectUpdates from './pages/LBWProjectUpdates.jsx';
import LBWAssessments from './pages/LBWAssessments.jsx';
import LBWCoaching from './pages/LBWCoaching.jsx';
import LBWMainLanding from './pages/LBWMainLanding.jsx';
import LBWDashboard from './pages/LBWDashboard.jsx';
import LBWProgress from './pages/LBWProgress.jsx';
import LBWPID from './pages/LBWPID.jsx';
import LBWContent from './pages/LBWContent.jsx';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import ActivationPending from './components/auth/ActivationPending';

import SuperAdminPanel from './components/admin/SuperAdminPanel';
import EngineerDashboard from './components/engineer/EngineerDashboard';
import SubscriptionManager from './components/engineer/SubscriptionManager';
import SupervisorDashboard from './components/supervisor/SupervisorDashboard';
import DashboardRouter from './components/DashboardRouter';
import ErrorBoundary from './components/ErrorBoundary';
import DevelopmentModeHelper from './components/auth/DevelopmentModeHelper';
import EnvironmentCheck from './components/debug/EnvironmentCheck';


function App() {
  console.log('START: AIMS App component loading...');

  // Test Supabase connection on app startup
  useEffect(() => {
    const runConnectionTest = async () => {
      console.log('START: AIMS (Asset Information Management System) Starting...');
      await testSupabaseConnection();
    };

    runConnectionTest();
  }, []);

  // Add global error handler
  React.useEffect(() => {
    const handleError = (event) => {
      console.error('ALERT: Global error caught:', event.error);
      
      // Check if it's a navigation/routing error
      if (event.error && (
        event.error.message.includes('Loading chunk') ||
        event.error.message.includes('Failed to fetch') ||
        event.error.message.includes('Cannot read properties') ||
        event.error.name === 'ChunkLoadError'
      )) {
        console.error('ALERT: Navigation/Chunk loading error detected');
        // Force reload to clear any stale state
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    };
    
    const handleUnhandledRejection = (event) => {
      console.error('ALERT: Unhandled promise rejection:', event.reason);
      
      // Check for common navigation errors
      if (event.reason && (
        String(event.reason).includes('Loading chunk') ||
        String(event.reason).includes('Failed to fetch') ||
        String(event.reason).includes('Cannot read properties')
      )) {
        console.error('ALERT: Navigation promise rejection detected');
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
          <div className="App w-full min-h-screen overflow-x-hidden bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200">
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/science" element={<Science />} />
            <Route path="/technicians" element={<Technicians />} />
            <Route path="/lbw" element={<LBWMainLanding />} />
            <Route path="/lbw-updates" element={<LBWProjectUpdates />} />
            <Route path="/assessments" element={<LBWAssessments />} />
            <Route path="/coaching" element={<LBWCoaching />} />
            <Route path="/lbw/dashboard" element={<LBWDashboard />} />
            <Route path="/lbw/progress" element={<LBWProgress />} />
            <Route path="/lbw/pid" element={<LBWPID />} />
            <Route path="/lbw/content" element={<LBWContent />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/activation-pending" element={<ActivationPending />} />
            <Route path="/forgot-password" element={<ForgotPasswordForm />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />

            {/* Debug Routes */}
            <Route path="/env-check" element={<EnvironmentCheck />} />

            {/* Protected Routes */}

            {/* Super Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clinics"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/algorithm-processor"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/alerts"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminPanel />
                </ProtectedRoute>
              }
            />

            {/* Engineer Routes (formerly Clinic Routes) */}
            <Route
              path="/clinic"
              element={
                <ProtectedRoute requiredRole="clinic_admin">
                  <EngineerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic/patients"
              element={
                <ProtectedRoute requiredRole="clinic_admin">
                  <EngineerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic/reports"
              element={
                <ProtectedRoute requiredRole="clinic_admin">
                  <EngineerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic/subscription"
              element={
                <ProtectedRoute requiredRole="clinic_admin">
                  <EngineerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic/usage"
              element={
                <ProtectedRoute requiredRole="clinic_admin">
                  <EngineerDashboard />
                </ProtectedRoute>
              }
            />

            {/* Supervisor Routes (formerly Patient Routes) */}
            <Route
              path="/patient"
              element={
                <ProtectedRoute requiredRole="patient">
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/patient-dashboard"
              element={
                <ProtectedRoute requiredRole="patient">
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />

            {/* Smart Dashboard Route - redirects based on user role */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute>
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/activity"
              element={
                <ProtectedRoute>
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/notifications"
              element={
                <ProtectedRoute>
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/reports"
              element={
                <ProtectedRoute requiredRole="patient">
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/resources"
              element={
                <ProtectedRoute requiredRole="patient">
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/journey"
              element={
                <ProtectedRoute requiredRole="patient">
                  <SupervisorDashboard />
                </ProtectedRoute>
              }
            />



            {/* Catch all route - redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#374151',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb',
              },
              success: {
                iconTheme: {
                  primary: '#323956',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />

          {/* Development Mode Helper */}
          <DevelopmentModeHelper />
          </div> {/* Close main App div */}
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
